import { Box, Button, Card, Stack, type Theme, Typography } from "@mui/material";
import { ActionButton, DataTable, type DataTableColumn, Empty, SearchField, colors } from "@zfp-akademie-a-s/zfp-design";
import { CheckCircleIcon, InfoIcon, SearchIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { useGetInstruments } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { Instrument } from "Infrastructure/ApiZfp/Schema/instrument";
import useDebounce from "Infrastructure/Hooks/useDebounce";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext, useState } from "react";
import { NsureSelectionContext, NsureSelectionItemType } from "./InvestmentsNsureSelectionContext";
import { useInstrumentTypeLabel } from "./useInstrumentTypeLabel";

export type InvestmentInstrument = Pick<
  Instrument,
  | "instrumentID"
  | "name"
  | "isin"
  | "ticker"
  | "isEsg"
  | "minInvestmentHorizon"
  | "srri"
  | "sri"
  | "instrumentType"
  | "instrumentRelations"
  | "customerInstruments"
  | "instrumentClass"
>;

// The package's table in the package's card (its border and clipping), in the portfolio cards' radius:
// 44px rows under a light header that stays put while the rows scroll, the chosen row (its check
// carries data-selected) in the lightest brand tint. Below lg the evaluation panel sits under the list, so the
// list gives up half its height to keep it in view.
const tableBoxSx = (theme: Theme) => ({
  borderRadius: Radius.M,
  "& div:has(> .MuiTable-root)": {
    maxHeight: "30vh",
    overflowY: "auto",
    [theme.breakpoints.up("lg")]: { maxHeight: "60vh" },
  },
  // no top margin, so the header meets the card's edge
  "& .MuiTable-root": { mt: 0, tableLayout: "fixed" },
  "& .MuiTableCell-root": { height: 44, py: 0, px: Spacing.M },
  "& .MuiTableCell-head": {
    position: "sticky",
    top: 0,
    zIndex: 1,
    height: 36,
    typography: "caption",
    fontWeight: 500,
    bgcolor: colors.backgroundLight,
  },
  "& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root": { borderBottom: 0 },
  "& .MuiTableRow-root:has([data-selected])": { bgcolor: colors.brandLighter },
});

export const InvestmentsNsureInstrumentSearch = () => {
  const { t } = useResource();
  const getInstrumentTypeLabel = useInstrumentTypeLabel();
  const { selection, setSelection } = useContext(NsureSelectionContext);
  const [query, setQuery] = useState("");

  const debouncedQuery = useDebounce(query.trim(), 300);
  const { data, isLoading } = useGetInstruments({ query: debouncedQuery || undefined, orderBy: "name", orderByDirection: "Asc" });

  const instruments: InvestmentInstrument[] = (data?.items ?? []).map((i) => ({
    instrumentID: i.instrumentID,
    isin: i.isin,
    ticker: i.ticker,
    name: i.name,
    isEsg: i.isEsg,
    minInvestmentHorizon: i.minInvestmentHorizon,
    srri: i.srri,
    sri: i.sri,
    instrumentType: i.instrumentType,
    instrumentRelations: i.instrumentRelations ?? [],
    instrumentClass: i.instrumentClass,
    customerInstruments: i.customerInstruments,
  }));

  const handleSelect = (instrument: InvestmentInstrument) => {
    const customerInstrumentPerformance =
      instrument.customerInstruments && instrument.customerInstruments?.length > 0
        ? instrument.customerInstruments[0].expectedReturn
        : null;
    const instrumentClassPerformance = instrument.instrumentClass?.expectedReturn ?? null;
    setSelection({
      id: instrument.instrumentID,
      type: NsureSelectionItemType.Instrument,
      expectedPerformance: customerInstrumentPerformance ?? instrumentClassPerformance,
      name: instrument.name,
      instrumentType: instrument.instrumentType,
      strategyInstruments: [{ id: instrument.instrumentID, name: instrument.name ?? null, participationInPercentage: 100 }],
      strategyInstrumentsEvaluation: [{ id: instrument.instrumentID, name: instrument.name ?? null, participationInPercentage: 100 }],
      isin: instrument.isin,
      ticker: instrument.ticker,
      esg: instrument.isEsg ? 100 : 0,
      minInvestmentHorizon: instrument.minInvestmentHorizon,
      srri: instrument.srri,
      sri: instrument.sri,
      instrumentRelations: instrument.instrumentRelations,
    });
  };

  const selectedInstrumentId = selection?.type === NsureSelectionItemType.Instrument ? selection.id : null;

  const columns: DataTableColumn<InvestmentInstrument>[] = [
    {
      id: "info",
      header: null,
      width: 48,
      renderCell: (instrument) => (
        <ActionButton
          label={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentDetail.InstrumentInfo)}
          icon={<InfoIcon />}
          description={
            <Box component="span" sx={{ display: "grid", gridTemplateColumns: "auto 1fr", rowGap: Spacing.XS, columnGap: Spacing.L }}>
              <span>{t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Name)}</span>
              <span>{instrument.name}</span>
              <span>{t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Isin)}</span>
              <span>{instrument.isin}</span>
              <span>{t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Symbol)}</span>
              <span>{instrument.ticker}</span>
            </Box>
          }
        />
      ),
    },
    {
      id: "name",
      header: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Name),
      renderCell: (instrument) => (
        <Typography
          variant="body2"
          fontWeight={selectedInstrumentId === instrument.instrumentID ? 500 : 400}
          noWrap
          title={instrument.name}
        >
          {instrument.name}
        </Typography>
      ),
    },
    {
      id: "esg",
      header: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Esg),
      width: 56,
      align: "center",
      renderCell: (instrument) => (
        <Typography variant="caption">{instrument.isEsg ? t(Resources.Common.Yes) : t(Resources.Common.No)}</Typography>
      ),
    },
    {
      id: "horizon",
      header: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Horizon),
      width: 72,
      align: "center",
      renderCell: (instrument) => <Typography variant="caption">{instrument.minInvestmentHorizon}</Typography>,
    },
    {
      id: "type",
      header: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Type),
      width: 96,
      align: "center",
      renderCell: (instrument) => <Typography variant="caption">{getInstrumentTypeLabel(instrument.instrumentType)}</Typography>,
    },
    {
      id: "select",
      header: null,
      width: 88,
      align: "center",
      renderCell: (instrument) =>
        selectedInstrumentId === instrument.instrumentID ? (
          // marks the row as chosen for tableBoxSx
          <CheckCircleIcon data-selected sx={{ color: "primary.main", fontSize: 18, verticalAlign: "middle" }} />
        ) : (
          <Button variant="text" size="small" onClick={() => handleSelect(instrument)}>
            {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentSearch.SelectButton)}
          </Button>
        ),
    },
  ];

  return (
    <Stack spacing={Spacing.L} sx={{ flex: 1, minWidth: 0 }}>
      <SearchField
        fullWidth
        placeholder={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentSearch.SearchPlaceholder)}
        ariaLabel={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentSearch.SearchEmptyTitle)}
        clearLabel={t(Resources.Common.ClearSearch)}
        value={query}
        onChange={setQuery}
      />

      {!isLoading && instruments.length === 0 ? (
        <Empty
          iconSize={40}
          icon={<SearchIcon sx={{ color: "text.disabled" }} />}
          title={t(
            debouncedQuery
              ? Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentSearch.NoResultsTitle
              : Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentSearch.SearchEmptyTitle,
          )}
          text={t(
            debouncedQuery
              ? Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentSearch.NoResultsText
              : Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentSearch.SearchEmptyText,
          )}
        />
      ) : (
        <Card sx={tableBoxSx}>
          <DataTable
            size="small"
            columns={columns}
            rows={instruments}
            getRowId={(instrument) => instrument.instrumentID}
            // the whole row picks the instrument, as "Vybrat" does, with the package's clickable row (hover, focus,
            // Enter); the chosen one again is no change, so it doesn't set off a new evaluation
            onRowClick={(instrument) => instrument.instrumentID !== selectedInstrumentId && handleSelect(instrument)}
            isLoading={isLoading}
            aria-label={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.SolutionSelect.Title)}
          />
        </Card>
      )}
    </Stack>
  );
};
